/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import {
  generateAnalogousColor,
  generateComplementaryColor,
} from "@/lib/utils/colors/color-harmony";
import {
  darken,
  desaturate,
  generateColorScale,
  generateColorScaleFromHsl,
  getContrastingTextColor,
  hexToHsl,
  isValidHexColor,
  lighten,
  normalizeHexColor,
  parseHsl,
} from "@/lib/utils/colors/color-utils";
import {
  calculateDarkModeColor,
  calculateDarkModeForeground,
  generateDarkModeScheme,
} from "@/lib/utils/colors/dark-mode-colors";
import { logger } from "@/lib/utils/logger";
import { generateHarmonizedSemanticColors } from "@/lib/utils/colors/semantic-colors";
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from "../constants";
import { refreshDesignTokenCache } from "@/components/ui/foundation/design-tokens";
import { applyThemeVars } from "./theme-style-tag";

/**
 * Apply primary color and scale to vars map
 */
function applyPrimaryColors(
  vars: Record<string, string>,
  primaryHsl: string,
  primaryParsed: ReturnType<typeof parseHsl>,
  primaryScale: ReturnType<typeof generateColorScale>,
) {
  vars["--primary"] = primaryHsl;
  vars["--primary-foreground"] = getContrastingTextColor(primaryParsed);
  Object.entries(primaryScale).forEach(([shade, hsl]) => {
    vars[`--primary-${shade}`] = hsl;
  });
}

/**
 * Apply secondary color and scale to vars map
 */
function applySecondaryColors(
  vars: Record<string, string>,
  secondaryHsl: string,
  secondaryParsed: ReturnType<typeof parseHsl>,
  secondaryScale: ReturnType<typeof generateColorScale>,
) {
  vars["--secondary"] = secondaryHsl;
  vars["--secondary-foreground"] = getContrastingTextColor(secondaryParsed);
  Object.entries(secondaryScale).forEach(([shade, hsl]) => {
    vars[`--secondary-${shade}`] = hsl;
  });
}

/**
 * Apply tertiary color and scale to vars map
 */
function applyTertiaryColors(
  vars: Record<string, string>,
  primaryParsed: ReturnType<typeof parseHsl>,
) {
  const tertiaryHsl = generateComplementaryColor(primaryParsed, {
    saturationAdjust: -10,
    maintainLightness: true,
  });
  const tertiaryParsed = parseHsl(tertiaryHsl);
  const tertiaryScale = generateColorScaleFromHsl(tertiaryHsl);

  vars["--tertiary"] = tertiaryHsl;
  vars["--tertiary-foreground"] = getContrastingTextColor(tertiaryParsed);
  Object.entries(tertiaryScale).forEach(([shade, hsl]) => {
    vars[`--tertiary-${shade}`] = hsl;
  });

  return { tertiaryHsl, tertiaryParsed };
}

/**
 * Apply state colors (hover, active, disabled) for primary, secondary, and tertiary
 */
function applyStateColors(
  vars: Record<string, string>,
  primaryHsl: string,
  secondaryHsl: string,
  tertiaryHsl: string,
) {
  vars["--primary-hover"] = lighten(primaryHsl, 5);
  vars["--primary-active"] = darken(primaryHsl, 8);
  vars["--primary-disabled"] = desaturate(primaryHsl, 50);

  vars["--secondary-hover"] = lighten(secondaryHsl, 5);
  vars["--secondary-active"] = darken(secondaryHsl, 8);
  vars["--secondary-disabled"] = desaturate(secondaryHsl, 50);

  vars["--tertiary-hover"] = lighten(tertiaryHsl, 5);
  vars["--tertiary-active"] = darken(tertiaryHsl, 8);
  vars["--tertiary-disabled"] = desaturate(tertiaryHsl, 50);
}

/**
 * Apply accent and ring colors
 */
function applyAccentColors(
  vars: Record<string, string>,
  primaryHsl: string,
  primaryParsed: ReturnType<typeof parseHsl>,
) {
  vars["--accent"] = primaryHsl;
  vars["--accent-foreground"] = getContrastingTextColor(primaryParsed);
  vars["--ring"] = primaryHsl;
}

/**
 * Apply semantic colors and scales
 */
function applySemanticColors(vars: Record<string, string>, primary: string) {
  const semanticColors = generateHarmonizedSemanticColors(primary);
  const semanticScales = {
    success: generateColorScaleFromHsl(semanticColors.success),
    warning: generateColorScaleFromHsl(semanticColors.warning),
    error: generateColorScaleFromHsl(semanticColors.error),
    info: generateColorScaleFromHsl(semanticColors.info),
  };

  Object.entries(semanticScales).forEach(([semanticName, scale]) => {
    Object.entries(scale).forEach(([shade, hsl]) => {
      vars[`--${semanticName}-${shade}`] = hsl;
    });
  });

  Object.entries(semanticColors).forEach(([key, value]) => {
    if (key.endsWith("Foreground")) {
      const semanticName = key.replace("Foreground", "");
      vars[`--${semanticName}-foreground`] = value;
    } else if (
      key === "success" ||
      key === "warning" ||
      key === "error" ||
      key === "info"
    ) {
      vars[`--${key}`] = value;
      vars[`--${key}-hover`] = lighten(value, 5);
      vars[`--${key}-active`] = darken(value, 8);
      vars[`--${key}-disabled`] = desaturate(value, 50);
    }
  });

  return semanticColors;
}

/**
 * Apply dark mode adjustments
 */
function applyDarkModeColors(
  vars: Record<string, string>,
  primaryParsed: ReturnType<typeof parseHsl>,
  secondaryParsed: ReturnType<typeof parseHsl>,
  tertiaryHsl: string,
  semanticColors: ReturnType<typeof generateHarmonizedSemanticColors>,
) {
  const darkModeScheme = generateDarkModeScheme({
    primary: primaryParsed,
    secondary: secondaryParsed,
    accent: primaryParsed,
  });

  const darkTertiaryHsl = calculateDarkModeColor(tertiaryHsl, {
    lightnessAdjustment: -10,
    saturationAdjustment: -5,
  });
  const darkTertiaryParsed = parseHsl(darkTertiaryHsl);
  const darkTertiaryScale = generateColorScaleFromHsl(darkTertiaryHsl);

  vars["--primary"] = darkModeScheme.primary;
  vars["--primary-foreground"] = darkModeScheme.primaryForeground;
  vars["--secondary"] = darkModeScheme.secondary;
  vars["--secondary-foreground"] = darkModeScheme.secondaryForeground;

  vars["--tertiary"] = darkTertiaryHsl;
  vars["--tertiary-foreground"] =
    calculateDarkModeForeground(darkTertiaryParsed);
  Object.entries(darkTertiaryScale).forEach(([shade, hsl]) => {
    vars[`--tertiary-${shade}`] = hsl;
  });

  vars["--tertiary-hover"] = lighten(darkTertiaryHsl, 5);
  vars["--tertiary-active"] = darken(darkTertiaryHsl, 8);
  vars["--tertiary-disabled"] = desaturate(darkTertiaryHsl, 50);

  vars["--accent"] = darkModeScheme.accent;
  vars["--accent-foreground"] = darkModeScheme.accentForeground;
  vars["--ring"] = darkModeScheme.primary;

  vars["--background"] = darkModeScheme.background;
  vars["--foreground"] = darkModeScheme.foreground;
  vars["--card"] = darkModeScheme.card;
  vars["--card-foreground"] = darkModeScheme.cardForeground;
  vars["--border"] = darkModeScheme.border;
  vars["--input"] = darkModeScheme.input;
  vars["--muted"] = darkModeScheme.muted;
  vars["--muted-foreground"] = darkModeScheme.mutedForeground;

  const darkBackgroundParsed = parseHsl(darkModeScheme.background);
  const darkSurfaceHierarchy = {
    surface: `${darkBackgroundParsed.h} ${darkBackgroundParsed.s}% ${darkBackgroundParsed.l}%`,
    "surface-variant": `${darkBackgroundParsed.h} ${darkBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, darkBackgroundParsed.l + 2)))}%`,
    "surface-container-lowest": `${darkBackgroundParsed.h} ${darkBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, darkBackgroundParsed.l + 1)))}%`,
    "surface-container-low": `${darkBackgroundParsed.h} ${darkBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, darkBackgroundParsed.l + 2)))}%`,
    "surface-container": `${darkBackgroundParsed.h} ${darkBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, darkBackgroundParsed.l + 4)))}%`,
    "surface-container-high": `${darkBackgroundParsed.h} ${darkBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, darkBackgroundParsed.l + 6)))}%`,
    "surface-container-highest": `${darkBackgroundParsed.h} ${darkBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, darkBackgroundParsed.l + 8)))}%`,
  };

  Object.entries(darkSurfaceHierarchy).forEach(([surfaceName, hsl]) => {
    vars[`--${surfaceName}`] = hsl;
  });

  const darkSuccessParsed = parseHsl(semanticColors.success);
  const darkWarningParsed = parseHsl(semanticColors.warning);
  const darkErrorParsed = parseHsl(semanticColors.error);
  const darkInfoParsed = parseHsl(semanticColors.info);

  const darkSuccessHsl = `${darkSuccessParsed.h} ${darkSuccessParsed.s}% ${Math.round(Math.min(55, darkSuccessParsed.l + 5))}%`;
  const darkWarningHsl = `${darkWarningParsed.h} ${darkWarningParsed.s}% ${Math.round(Math.min(60, darkWarningParsed.l + 5))}%`;
  const darkErrorHsl = `${darkErrorParsed.h} ${darkErrorParsed.s}% ${Math.round(Math.min(55, darkErrorParsed.l + 5))}%`;
  const darkInfoHsl = `${darkInfoParsed.h} ${darkInfoParsed.s}% ${Math.round(Math.min(55, darkInfoParsed.l + 5))}%`;

  const darkSemanticScales = {
    success: generateColorScaleFromHsl(darkSuccessHsl),
    warning: generateColorScaleFromHsl(darkWarningHsl),
    error: generateColorScaleFromHsl(darkErrorHsl),
    info: generateColorScaleFromHsl(darkInfoHsl),
  };

  Object.entries(darkSemanticScales).forEach(([semanticName, scale]) => {
    Object.entries(scale).forEach(([shade, hsl]) => {
      vars[`--${semanticName}-${shade}`] = hsl;
    });
  });

  vars["--success"] = darkSuccessHsl;
  vars["--warning"] = darkWarningHsl;
  vars["--error"] = darkErrorHsl;
  vars["--info"] = darkInfoHsl;
  vars["--success-foreground"] = "0 0% 98%";
  vars["--warning-foreground"] = "0 0% 15%";
  vars["--error-foreground"] = "0 0% 98%";
  vars["--info-foreground"] = "0 0% 98%";
  vars["--destructive"] = darkErrorHsl;
  vars["--destructive-foreground"] = "0 0% 98%";
}

/**
 * Apply light mode defaults
 */
function applyLightModeColors(
  vars: Record<string, string>,
  semanticColors: ReturnType<typeof generateHarmonizedSemanticColors>,
) {
  vars["--success"] = semanticColors.success;
  vars["--success-foreground"] = semanticColors.successForeground;
  vars["--warning"] = semanticColors.warning;
  vars["--warning-foreground"] = semanticColors.warningForeground;
  vars["--error"] = semanticColors.error;
  vars["--error-foreground"] = semanticColors.errorForeground;
  vars["--info"] = semanticColors.info;
  vars["--info-foreground"] = semanticColors.infoForeground;
  vars["--destructive"] = semanticColors.error;
  vars["--destructive-foreground"] = semanticColors.errorForeground;

  vars["--background"] = "0 0% 100%";
  vars["--foreground"] = "0 0% 12%";
  vars["--card"] = "0 0% 96%";
  vars["--card-foreground"] = "0 0% 12%";
  vars["--border"] = "0 0% 90%";
  vars["--input"] = "0 0% 90%";
  vars["--muted"] = "0 0% 96%";
  vars["--muted-foreground"] = "0 0% 40%";

  const lightBackgroundParsed = parseHsl("0 0% 100%");
  const lightSurfaceHierarchy = {
    surface: `${lightBackgroundParsed.h} ${lightBackgroundParsed.s}% ${lightBackgroundParsed.l}%`,
    "surface-variant": `${lightBackgroundParsed.h} ${lightBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, lightBackgroundParsed.l - 2)))}%`,
    "surface-container-lowest": `${lightBackgroundParsed.h} ${lightBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, lightBackgroundParsed.l - 1)))}%`,
    "surface-container-low": `${lightBackgroundParsed.h} ${lightBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, lightBackgroundParsed.l - 2)))}%`,
    "surface-container": `${lightBackgroundParsed.h} ${lightBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, lightBackgroundParsed.l - 4)))}%`,
    "surface-container-high": `${lightBackgroundParsed.h} ${lightBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, lightBackgroundParsed.l - 6)))}%`,
    "surface-container-highest": `${lightBackgroundParsed.h} ${lightBackgroundParsed.s}% ${Math.round(Math.max(0, Math.min(100, lightBackgroundParsed.l - 8)))}%`,
  };

  Object.entries(lightSurfaceHierarchy).forEach(([surfaceName, hsl]) => {
    vars[`--${surfaceName}`] = hsl;
  });
}

/**
 * Apply extended palette — 5 supplementary hues for features needing more than
 * the 7 semantic colors (e.g. calendar event types, tag categories).
 */
function applyExtendedPalette(
  vars: Record<string, string>,
  primaryParsed: ReturnType<typeof parseHsl>,
  isDarkMode: boolean,
) {
  const dm = isDarkMode;
  const violet = generateAnalogousColor(primaryParsed, {
    direction: "cool",
    shift: 90,
    saturationAdjust: 10,
    lightnessAdjust: dm ? 10 : 0,
  });
  const teal = generateAnalogousColor(primaryParsed, {
    direction: "cool",
    shift: 150,
    saturationAdjust: -5,
    lightnessAdjust: dm ? 8 : -5,
  });
  const pink = generateAnalogousColor(primaryParsed, {
    direction: "warm",
    shift: 90,
    saturationAdjust: 5,
    lightnessAdjust: dm ? 10 : 0,
  });
  const orange = generateAnalogousColor(primaryParsed, {
    direction: "warm",
    shift: 45,
    saturationAdjust: 15,
    lightnessAdjust: dm ? 5 : 0,
  });
  const sky = generateAnalogousColor(primaryParsed, {
    direction: "cool",
    shift: 45,
    saturationAdjust: 10,
    lightnessAdjust: dm ? 5 : 0,
  });

  vars["--palette-violet"] = violet;
  vars["--palette-teal"] = teal;
  vars["--palette-pink"] = pink;
  vars["--palette-orange"] = orange;
  vars["--palette-sky"] = sky;
}

/**
 * Apply organization colors to CSS variables
 */
export function applyOrganizationColors(
  primaryColor: string | undefined,
  secondaryColor: string | undefined,
  isDarkMode: boolean,
): void {
  const primary =
    primaryColor && isValidHexColor(primaryColor)
      ? normalizeHexColor(primaryColor)
      : DEFAULT_PRIMARY_COLOR;
  const secondary =
    secondaryColor && isValidHexColor(secondaryColor)
      ? normalizeHexColor(secondaryColor)
      : DEFAULT_SECONDARY_COLOR;

  try {
    const primaryHsl = hexToHsl(primary);
    const secondaryHsl = hexToHsl(secondary);
    const primaryParsed = parseHsl(primaryHsl);
    const secondaryParsed = parseHsl(secondaryHsl);

    const primaryScale = generateColorScale(primary);
    const secondaryScale = generateColorScale(secondary);

    const vars: Record<string, string> = {};

    applyPrimaryColors(vars, primaryHsl, primaryParsed, primaryScale);
    applySecondaryColors(vars, secondaryHsl, secondaryParsed, secondaryScale);

    const { tertiaryHsl } = applyTertiaryColors(vars, primaryParsed);
    applyStateColors(vars, primaryHsl, secondaryHsl, tertiaryHsl);
    applyAccentColors(vars, primaryHsl, primaryParsed);

    const semanticColors = applySemanticColors(vars, primary);
    applyExtendedPalette(vars, primaryParsed, isDarkMode);

    if (isDarkMode) {
      applyDarkModeColors(
        vars,
        primaryParsed,
        secondaryParsed,
        tertiaryHsl,
        semanticColors,
      );
    } else {
      applyLightModeColors(vars, semanticColors);
    }

    applyThemeVars(vars);
    refreshDesignTokenCache();
  } catch (error) {
    logger.error("Failed to apply organization colors", {
      error: error instanceof Error ? error.message : String(error),
      primaryColor,
      secondaryColor,
    });
  }
}
